import { Typography, Box, Divider, Alert, Button } from "@mui/material";
import {
    CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "../components/common/custom-component/ToastProvider";

import LoadingBackdrop from "../components/common/custom-component/LoadingBackdrop";
import DataContainer, {
    TableActionButton,
    TableActionGroup,
} from "../components/common/DataContainer";
import { useSearchFilter } from "../hooks/useSearchFilter";
import EnhancedConfirmationDialog from "../components/common/custom-component/EnhancedConfirmationDialog";
import SearchFilterBar from "../components/common/SearchFilterBar";
import CustomPagination from "../components/common/CustomPagination";
import { logger } from "helpers/logger";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
    addTransporterTruckThunk,
    deleteTransporterTruckThunk,
    editTransporterTruckThunk,
    fetchTrucksThunk,
    fetchDatabaseRecords,
} from "store/thunks/mdsThunks";

import { manageTrucksReducer, initialState } from "../reducers/manageTrucksReducer";
import { useTableColumns } from "../hooks/useTableColumns";

export default function ManageTrucksPage() {
    const { success: ToastSuccess, error: ToastError} = useToast();
    const dispatch = useDispatch();
    const [t] = useTranslation();
    const vehicleTypeLabelToIdRef = useRef({});
    const activeThunksRef = useRef([]);

    const [retryTrigger, setRetryTrigger] = useState(0);
    const [error, setError] = useState(null);

    const normalizeDropdownValue = (optionOrValue) => {
        if (optionOrValue && typeof optionOrValue === "object") {
            if ("value" in optionOrValue) return optionOrValue.value;
            if ("label" in optionOrValue) return optionOrValue.label;
        }
        return optionOrValue ?? "";
    };

    const selectedCompany = useSelector((state) => state.selectedCompany.data?.companyData);

    const [state, dispatchState] = useReducer(manageTrucksReducer, initialState);


    const handleRetry = () => {
        setRetryTrigger(prev => prev + 1);
    };

    const errorView = error ? (
        <Box sx={{ p: 4, mx: "auto", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <Alert severity="error" sx={{ width: "100%", maxWidth: 600 }}>
                {error}
            </Alert>
            <Button variant="contained" color="primary" startIcon={<RefreshIcon />} onClick={handleRetry}>
                Retry
            </Button>
        </Box>
    ) : null;


    const displayData = useMemo(() => {
        return Array.isArray(state.data.trucks) ? state.data.trucks : state.data.trucks?.payload || [];
    }, [state.data.trucks]);

    // #region FETCH TRUCKS
    useEffect(() => {
        let promiseRef = null;

        const fetchData = async () => {
            // Reset states when retrying
            setError(null);
            dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: true } });

            promiseRef = dispatch(
                fetchTrucksThunk({
                    companyId: selectedCompany.companyId,
                })
            );

            const recordResult = await promiseRef;

            if (fetchTrucksThunk.fulfilled.match(recordResult)) {
                const records = recordResult?.payload?.data;
                dispatchState({ type: "SET_TRUCKS", payload: Array.isArray(records) ? records : [] });
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            } else if (fetchTrucksThunk.rejected.match(recordResult)) {
                // Check if it's not a cancellation
                if (recordResult.payload !== 'cancelled') {
                    setError(recordResult.payload || 'An error occurred while loading data');
                    logger.error('An error occurred while loading data');
                }
                dispatchState({ type: "SET_LOADING", payload: { key: "isLoadingBackdrop", value: false } });
            }
        };

        fetchData();

        // Cleanup: abort on unmount - just like saga!
        return () => {
            if (promiseRef) {
                logger.info('🛑 Component unmounting, aborting request');
                promiseRef.abort();
            }
        };

    }, [dispatch, retryTrigger]);

    useEffect(() => {
        return () => {
            activeThunksRef.current.forEach((thunk) => {
                if (thunk && typeof thunk.abort === "function") thunk.abort();
            });
            activeThunksRef.current = [];
        };
    }, []);

    // #endregion

    // #region SEARCH & PAGINATION
    const { setSearchQuery, filteredData } = useSearchFilter(
        displayData,
        [
            "plateNumber",
            "type",
            "subType",
            "size",
            "tareWeight",
            "capacity",
            "capacityUnit",
            "vehicleSerialNumber",
        ],
        null
    );

    const totalPages = Math.max(
        1,
        Math.ceil((filteredData?.length || 0) / state.table.recordsPerPage)
    );

    const pagedData = useMemo(() => {
        const start = (state.table.currentPage - 1) * state.table.recordsPerPage;
        const end = start + state.table.recordsPerPage;
        return (filteredData || []).slice(start, end);
    }, [filteredData, state.table.currentPage, state.table.recordsPerPage]);
    // #endregion

    // #region HELPER FUNCTIONS
    const getVehicleSubTypeOptions = useCallback(
        (vehicleType) => {
            if (!vehicleType) return [];
            const subList = state.ui.vehicleData?.[vehicleType] || [];
            return subList.map((s) => ({ value: s, label: t(s) }));
        },
        [state.ui.vehicleData, t]
    );

    const fetchVehicleTypes = useCallback(async () => {
        try {
            const thunkPromise = dispatch(fetchDatabaseRecords({ tableName: "tbl_vehicle_Type", condition: "", }));

            activeThunksRef.current.push(thunkPromise);

            const vehicleTypesData = await thunkPromise;

            activeThunksRef.current = activeThunksRef.current.filter((t) => t !== thunkPromise);

            if (fetchDatabaseRecords.rejected.match(vehicleTypesData)) {
                logger.error("Error fetching vehicle types:", vehicleTypesData.payload);
                return null;
            }

            const vehicleTypes = vehicleTypesData.payload?.data?.records;
            return vehicleTypes;
        } catch (error) {
            logger.error("Error in fetchVehicleTypes:", error);
            return null;
        }
    }, [dispatch]);

    const fetchUnits = useCallback(async () => {
        try {
            const thunkPromise = dispatch(fetchDatabaseRecords({ tableName: "tbl_units", condition: "", }));
            activeThunksRef.current.push(thunkPromise);
            const unitsData = await thunkPromise;
            activeThunksRef.current = activeThunksRef.current.filter((t) => t !== thunkPromise);
            if (fetchDatabaseRecords.rejected.match(unitsData)) {
                logger.error("Error fetching units:", unitsData.payload);
                return null;
            }
            const units = unitsData.payload?.data?.records;
            return units;
        } catch (error) {
            logger.error("Error in fetchUnits:", error);
            return null;
        }
    }, [dispatch]);

    const loadLookupData = useCallback(async () => {
        try {
            // VEHICLE TYPES
            dispatchState({
                type: "SET_LOADING",
                payload: { key: "isVehicleTypeLoading", value: true },
            });

            //Parallel API Call

            const [resTypes, resUnits] = await Promise.all([
                fetchVehicleTypes(),
                fetchUnits(),
            ]);

            const arr = Array.isArray(resTypes) ? resTypes : [];

            vehicleTypeLabelToIdRef.current = arr
                .filter((r) => r && r.id != null && r.label != null)
                .reduce((acc, r) => {
                    acc[String(r.label)] = Number(r.id);
                    return acc;
                }, {});

            const typeOptions = arr
                .filter((r) => r && r.label != null)
                .map((r) => ({
                    value: String(r.label),
                    label: String(r.label),
                }));

            dispatchState({
                type: "SET_VEHICLE_TYPE_OPTIONS",
                payload: typeOptions,
            });

            // UNITS
            dispatchState({
                type: "SET_LOADING",
                payload: { key: "isCapacityUnitLoading", value: true },
            });

            const arrU = Array.isArray(resUnits) ? resUnits : [];

            const unitOptions = arrU
                .filter((r) => r && r.label != null)
                .map((r) => ({
                    value: String(r.label),
                    label: String(r.label),
                }));

            dispatchState({
                type: "SET_CAPACITY_UNIT_OPTIONS",
                payload: unitOptions,
            });
        } catch (error) {
            logger.error("Failed to fetch lookup data:", error);
            ToastError(t("Failed to load lookup data"));
        } finally {
            dispatchState({
                type: "SET_LOADING",
                payload: { key: "isVehicleTypeLoading", value: false },
            });
            dispatchState({
                type: "SET_LOADING",
                payload: { key: "isCapacityUnitLoading", value: false },
            });
        }
    }, [dispatch, state.ui.vehicleTypeOptions, state.ui.capacityUnitOptions, t]);

    const fetchVehicleSubTypesByTypeLabel = useCallback(
        async (vehicleTypeLabel) => {
            try {
                if (!vehicleTypeLabel) return;

                const typeId = vehicleTypeLabelToIdRef.current?.[vehicleTypeLabel];
                if (!typeId) {
                    dispatchState({
                        type: "SET_VEHICLE_DATA",
                        payload: {
                            ...(state.ui.vehicleData || {}),
                            [vehicleTypeLabel]: [],
                        },
                    });
                    return;
                }

                dispatchState({
                    type: "SET_LOADING",
                    payload: { key: "isVehicleSubTypeLoading", value: true },
                });

                const resSub = await dispatch(
                    fetchDatabaseRecords({
                        tableName: "tbl_vehicle_subType",
                        condition: `vehicleTypeId=eq.${typeId}`,
                    })
                );

                if (resSub.meta?.requestStatus === "fulfilled") {
                    const raw = resSub.payload?.data?.records;
                    const arr = Array.isArray(raw) ? raw : [];

                    const list = arr
                        .filter((r) => r && r.label != null)
                        .map((r) => String(r.label));

                    const unique = Array.from(new Set(list));

                    dispatchState({
                        type: "SET_VEHICLE_DATA",
                        payload: {
                            ...(state.ui.vehicleData || {}),
                            [vehicleTypeLabel]: unique,
                        },
                    });
                }
            } catch (error) {
                logger.error("Failed to fetch vehicle subtypes:", error);
                ToastError(t("Failed to load vehicle subtypes"));
            } finally {
                dispatchState({
                    type: "SET_LOADING",
                    payload: { key: "isVehicleSubTypeLoading", value: false },
                });
            }
        },
        [dispatch, state.ui.vehicleData, t]
    );
    // #endregion

    // #region ADD TRUCK
    const handleAddClick = useCallback(async () => {
        await loadLookupData();

        dispatchState({
            type: "UPDATE_FORM_FIELD",
            payload: { formName: "addTruckForm", fieldName: "subType", value: "" },
        });

        dispatchState({ type: "OPEN_ADD_DIALOG" });
    }, [loadLookupData]);

    const handleCancelAdd = useCallback(() => {
        dispatchState({ type: "CLOSE_ADD_DIALOG" });
    }, []);

    const handleConfirmAdd = useCallback(
        async (formData) => {
            dispatchState({
                type: "SET_LOADING",
                payload: { key: "isLoadingBackdrop", value: true },
            });

            try {
                if (!selectedCompany?.companyId) {
                    ToastError(t("Company not selected"));
                    return;
                }

                const payload = {
                    ...formData,
                    plateNumber: formData.plateNumber ? formData.plateNumber.replace(/\s/g, "").toUpperCase().replace(/^([A-Z]{3})([0-9]{4})$/, "$1 $2") : formData.plateNumber,
                    type: state.forms.addTruckForm.type,
                    subType: state.forms.addTruckForm.subType,
                };

                const result = await dispatch(
                    addTransporterTruckThunk({
                        relatedCompanyId: selectedCompany.companyId,
                        data: payload,
                    })
                );

                if (addTransporterTruckThunk.rejected.match(result)) {
                    ToastError(t("An error occured, please try again later."));
                    return;
                }

                ToastSuccess(t("Success"));
                const records = await dispatch(fetchTrucksThunk({ companyId: selectedCompany.companyId }));
                dispatchState({ type: "SET_TRUCKS", payload: Array.isArray(records?.payload?.data) ? records?.payload?.data : [] });
                dispatchState({ type: "CLOSE_ADD_DIALOG" });
            } catch (error) {
                ToastError(t("An error occured, please try again later."));
            } finally {
                dispatchState({
                    type: "SET_LOADING",
                    payload: { key: "isLoadingBackdrop", value: false },
                });
            }
        },
        [dispatch, selectedCompany?.companyId, state.forms.addTruckForm.type, state.forms.addTruckForm.subType, t]
    );
    // #endregion

    // #region EDIT TRUCK
    const handleEditClick = useCallback(
        async (row) => {
            await loadLookupData();

            const editTruckInitialFormData = {
                plateNumber: row?.plateNumber ?? "",
                type: row?.type ?? "",
                subType: row?.subType ?? "",
                size: row?.size ?? "",
                tareWeight: row?.tareWeight ?? "",
                capacity: row?.capacity ?? "",
                capacityUnit: row?.capacityUnit ?? "",
                vehicleSerialNumber: row?.vehicleSerialNumber ?? "",
            };

            dispatchState({
                type: "OPEN_EDIT_DIALOG",
                payload: {
                    record: row,
                    editFormData: editTruckInitialFormData,
                    initialEditTruckForm: editTruckInitialFormData,
                },
            });

            if (editTruckInitialFormData.type) {
                await fetchVehicleSubTypesByTypeLabel(editTruckInitialFormData.type);
            }
        },
        [loadLookupData, fetchVehicleSubTypesByTypeLabel]
    );

    const handleCancelEdit = useCallback(() => {
        dispatchState({ type: "CLOSE_EDIT_DIALOG" });
    }, []);

    const handleConfirmEdit = useCallback(
        async (formData) => {
            if (!selectedCompany?.companyId) {
                ToastError(t("Company not selected"));
                return;
            }

            dispatchState({
                type: "SET_LOADING",
                payload: { key: "isLoadingBackdrop", value: true },
            });

            try {
                const payload = {
                    ...formData,
                    plateNumber: formData.plateNumber ? formData.plateNumber.replace(/\s/g, "").toUpperCase().replace(/^([A-Z]{3})([0-9]{4})$/, "$1 $2") : formData.plateNumber,
                    type: state.forms.editTruckForm.type,
                    subType: state.forms.editTruckForm.subType,
                };

                const result = await dispatch(
                    editTransporterTruckThunk({
                        truckId: state.dialogs.selectedRecord.id,
                        data: payload,
                    })
                );

                if (editTransporterTruckThunk.rejected.match(result)) {
                    ToastError(t("An error occured, please try again later."));
                    return;
                }

                ToastSuccess(t("Success"));
                const records = await dispatch(fetchTrucksThunk({ companyId: selectedCompany.companyId }));
                dispatchState({ type: "SET_TRUCKS", payload: Array.isArray(records?.payload?.data) ? records?.payload?.data : [] });
                dispatchState({ type: "CLOSE_EDIT_DIALOG" });
            } catch (error) {
                ToastError(t("An error occured, please try again later."));
            } finally {
                dispatchState({
                    type: "SET_LOADING",
                    payload: { key: "isLoadingBackdrop", value: false },
                });
            }
        },
        [dispatch, selectedCompany?.companyId, state.dialogs.selectedRecord, state.forms.editTruckForm.type, state.forms.editTruckForm.subType, t]
    );
    // #endregion

    // #region VIEW TRUCK
    const handleViewClick = useCallback((row) => {
        const viewTruckInitialFormData = {
            plateNumber: row?.plateNumber ?? "",
            type: row?.type ?? "",
            subType: row?.subType ?? "",
            size: row?.size ?? "",
            tareWeight: row?.tareWeight ?? "",
            capacity: row?.capacity ?? "",
            capacityUnit: row?.capacityUnit ?? "",
            vehicleSerialNumber: row?.vehicleSerialNumber ?? "",
        };

        dispatchState({
            type: "OPEN_VIEW_DIALOG",
            payload: {
                record: row,
                viewFormData: viewTruckInitialFormData,
            },
        });
    }, []);

    const handleCancelView = useCallback(() => {
        dispatchState({ type: "CLOSE_VIEW_DIALOG" });
    }, []);
    // #endregion

    // #region DELETE TRUCK
    const handleDeleteClick = useCallback((row) => {
        dispatchState({ type: "OPEN_DELETE_DIALOG", payload: { ...row } });
    }, []);

    const handleCancelDelete = useCallback(() => {
        dispatchState({ type: "CLOSE_DELETE_DIALOG" });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!state.dialogs.selectedRecord?.id) {
            dispatchState({ type: "CLOSE_DELETE_DIALOG" });
            return;
        }

        dispatchState({
            type: "SET_LOADING",
            payload: { key: "isLoadingBackdrop", value: true },
        });

        try {
            const result = await dispatch(
                deleteTransporterTruckThunk({
                    truckId: state.dialogs.selectedRecord.id,
                })
            );

            if (deleteTransporterTruckThunk.rejected.match(result)) {
                ToastError(t("An error occured, please try again later."));
                return;
            }

            ToastSuccess(t("Deleted"));
            const records = await dispatch(fetchTrucksThunk({ companyId: selectedCompany.companyId }));
            dispatchState({ type: "SET_TRUCKS", payload: Array.isArray(records?.payload?.data) ? records?.payload?.data : [] });
            dispatchState({ type: "CLOSE_DELETE_DIALOG" });
        } catch (error) {
            ToastError(t("An error occured, please try again later."));
        } finally {
            dispatchState({
                type: "SET_LOADING",
                payload: { key: "isLoadingBackdrop", value: false },
            });
        }
    }, [dispatch, selectedCompany?.companyId, state.dialogs.selectedRecord, t]);
    // #endregion

    // #region MANAGE TRUCKS TABLE COLUMNS
    // Memoize actions to prevent unnecessary recalculations
    const actions = useMemo(() => ({
        onView: handleViewClick,
        onEdit: handleEditClick,
        onDelete: handleDeleteClick,
    }), [handleViewClick, handleEditClick, handleDeleteClick]);

    // Use the dynamic columns hook
    const { columns } = useTableColumns('ManageTrucks', {
        actions,
    });
    // #endregion

    // #region RETURN COMPONENT
    if (error) return errorView;
    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h3" gutterBottom color="primary">
                {t("Manage Trucks")}
            </Typography>

            <Typography variant="body1" color="text.secondary" paragraph>
                {t("This is a Manage Trucks page.")}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {!displayData || displayData.length === 0 ? (
                <DataContainer
                    data={[]}
                    columns={columns}
                    emptyStateIcon={CalendarIcon}
                    emptyStateTitle={t("No trucks found")}
                    emptyStateSubtitle={t("Start adding related trucks to your company")}
                />
            ) : (
                <>
                    <Box sx={{ p: 2 }}>
                        <Box sx={{ my: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                            <TableActionGroup
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    gap: 1,
                                }}
                            >
                                <TableActionButton
                                    text={t("Add New Truck")}
                                    variant="contained"
                                    onClick={() => handleAddClick()}
                                />
                            </TableActionGroup>

                            <SearchFilterBar
                                mode="frontend"
                                searchValue={state.table.searchInput}
                                onSearchChange={(v) => {
                                    dispatchState({ type: "SET_SEARCH_INPUT", payload: v });
                                    setSearchQuery(v);
                                }}
                                showFilterButton={false}
                                showSearchButton={false}
                                searchPlaceholder={t("Search trucks.")}
                            />
                        </Box>

                        <DataContainer
                            data={pagedData}
                            columns={columns}
                            showRowNumbers
                            maxHeight={600}
                            stickyHeader
                        />
                    </Box>

                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <CustomPagination
                            currentPage={state.table.currentPage}
                            totalPages={totalPages}
                            onPageChange={(e, v) =>
                                dispatchState({
                                    type: "SET_CURRENT_PAGE",
                                    payload: v,
                                })
                            }
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </>
            )}

            {/* DELETE TRUCK */}
            <EnhancedConfirmationDialog
                open={state.dialogs.deleteConfirm}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={t("Remove Truck")}
                message={t(
                    "Are you sure you want to remove this truck? This action cannot be undone."
                )}
                confirmText={t("DELETE")}
                confirmColor="error"
                cancelText={t("Cancel")}
            />

            {/* EDIT TRUCK */}
            <EnhancedConfirmationDialog
                open={state.dialogs.editConfirm}
                onClose={handleCancelEdit}
                onConfirm={handleConfirmEdit}
                title={t("Confirm Edit")}
                confirmText={t("Edit")}
                cancelText={t("Cancel")}
                showMessage={false}
                returnChangedFieldsOnly={false}
                defaultValues={state.forms.editTruckForm}
                fields={[
                    {
                        name: "plateNumber",
                        label: "Plate Number",
                        type: "text",
                        required: true,
                        regex: /^[a-zA-Z]{3} ?[0-9]{4}$/,
                        regexErrorMessage: t(
                            "Plate Number must be in the format ABC 1234 or ABC1234 (3 letters followed by 4 numbers)."
                        ),
                    },
                    {
                        name: "type",
                        label: "Vehicle Type",
                        type: "dropdown",
                        required: true,
                        options: state.ui.vehicleTypeOptions,
                        isLoading: state.loading.isVehicleTypeLoading,
                        onChange: async (value) => {
                            const v = normalizeDropdownValue(value);

                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: {
                                    formName: "editTruckForm",
                                    fieldName: "type",
                                    value: v,
                                },
                            });

                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: {
                                    formName: "editTruckForm",
                                    fieldName: "subType",
                                    value: "",
                                },
                            });

                            if (v) {
                                await fetchVehicleSubTypesByTypeLabel(v);
                            }
                        },
                    },
                    {
                        name: "subType",
                        label: "Vehicle Subtype",
                        type: "dropdown",
                        required: true,
                        options: getVehicleSubTypeOptions(
                            state.forms.editTruckForm.type || state.dialogs.selectedRecord?.type
                        ),
                        disabled: !(
                            state.forms.editTruckForm.type || state.dialogs.selectedRecord?.type
                        ),
                        isLoading: state.loading.isVehicleSubTypeLoading,
                        onChange: (value) => {
                            const v = normalizeDropdownValue(value);
                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: {
                                    formName: "editTruckForm",
                                    fieldName: "subType",
                                    value: v,
                                },
                            });
                        },
                    },
                    {
                        name: "size",
                        label: "Vehicle Gross Weight",
                        type: "number",
                        regex: /^(?:([5-9]\d\d|\d{4,}))?$/,
                        regexErrorMessage: t(
                            "Gross Weight must be a valid number and must be 500 or more"
                        ),
                    },
                    {
                        name: "tareWeight",
                        label: "Vehicle Tare Weight",
                        type: "number",
                        regex: /^((5\d\d|\d{4,})(\.\d+)?)$/,
                        regexErrorMessage: t(
                            "Tare Weight must be a valid number and must be 500 or more"
                        ),
                    },
                    {
                        name: "capacity",
                        label: "Vehicle Load Capacity",
                        type: "number",
                        required: true,
                        regex: /^(?:\d+(\.\d+)?|\.\d+)$|^$/,
                        regexErrorMessage: t("Capacity must be a positive number."),
                    },
                    {
                        name: "capacityUnit",
                        label: "Vehicle Load Capacity UOM",
                        type: "dropdown",
                        required: true,
                        options: state.ui.capacityUnitOptions,
                        isLoading: state.loading.isCapacityUnitLoading,
                    },
                    {
                        name: "vehicleSerialNumber",
                        label: "Vehicle Serial Number",
                        type: "text",
                        required: true,
                        regex: /^[0-9]{8,}$/,
                        regexErrorMessage: t(
                            "Vehicle Serial Number must contain only English digits and be at least 8 characters long."
                        ),
                    },
                ]}
            />

            {/* VIEW TRUCK */}
            <EnhancedConfirmationDialog
                open={state.dialogs.viewConfirm}
                onClose={handleCancelView}
                onConfirm={handleCancelView}
                title={t("View Truck")}
                confirmText={t("OK")}
                cancelText={t("Close")}
                showMessage={false}
                defaultValues={state.forms.viewTruckForm}
                fields={[
                    { name: "plateNumber", label: "Plate Number", type: "text", disabled: true },
                    { name: "type", label: "Vehicle Type", type: "text", disabled: true },
                    { name: "subType", label: "Vehicle Subtype", type: "text", disabled: true },
                    { name: "size", label: "Vehicle Gross Weight", type: "text", disabled: true },
                    { name: "tareWeight", label: "Vehicle Tare Weight", type: "text", disabled: true },
                    { name: "capacity", label: "Vehicle Load Capacity", type: "text", disabled: true },
                    { name: "capacityUnit", label: "Vehicle Load Capacity UOM", type: "text", disabled: true },
                    { name: "vehicleSerialNumber", label: "Vehicle Serial Number", type: "text", disabled: true },
                ]}
            />

            {/* ADD TRUCK */}
            <EnhancedConfirmationDialog
                open={state.dialogs.addConfirm}
                onClose={handleCancelAdd}
                onConfirm={handleConfirmAdd}
                title={t("Add New Truck")}
                message={
                    <Box component="span" sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Typography component="span" variant="body2" color="text.secondary">
                            {t("Add truck to your company")}
                        </Typography>
                    </Box>
                }
                cancelText={t("Cancel")}
                confirmText={t("Confirm")}
                confirmColor="primary"
                showMessage={true}
                defaultValues={state.forms.addTruckForm || initialState.forms.addTruckForm}
                fields={[
                    {
                        name: "plateNumber",
                        label: "Plate Number",
                        type: "text",
                        required: true,
                        regex: /^[a-zA-Z]{3} ?[0-9]{4}$/,
                        regexErrorMessage: t(
                            "Plate Number must be in the format ABC 1234 or ABC1234 (3 letters followed by 4 numbers)."
                        ),
                        onChange: (value) =>
                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addTruckForm", fieldName: "plateNumber", value },
                            }),
                    },
                    {
                        name: "type",
                        label: "Vehicle Type",
                        type: "dropdown",
                        required: true,
                        options: state.ui.vehicleTypeOptions,
                        isLoading: state.loading.isVehicleTypeLoading,
                        onChange: async (value) => {
                            const v = normalizeDropdownValue(value);

                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addTruckForm", fieldName: "type", value: v },
                            });

                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addTruckForm", fieldName: "subType", value: "" },
                            });

                            if (v) {
                                await fetchVehicleSubTypesByTypeLabel(v);
                            }
                        },
                    },
                    {
                        name: "subType",
                        label: "Vehicle Subtype",
                        type: "dropdown",
                        required: true,
                        options: getVehicleSubTypeOptions(state.forms.addTruckForm.type),
                        disabled: !state.forms.addTruckForm.type,
                        isLoading: state.loading.isVehicleSubTypeLoading,
                        onChange: (value) => {
                            const v = normalizeDropdownValue(value);
                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addTruckForm", fieldName: "subType", value: v },
                            });
                        },
                    },
                    {
                        name: "size",
                        label: "Vehicle Gross Weight",
                        type: "number",
                        regex: /^(?:([5-9]\d\d|\d{4,}))?$/,
                        regexErrorMessage: t(
                            "Gross Weight must be a valid number and must be 500 or more"
                        ),
                        onChange: (value) =>
                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addTruckForm", fieldName: "size", value },
                            }),
                    },
                    {
                        name: "tareWeight",
                        label: "Vehicle Tare Weight",
                        type: "number",
                        regex: /^((5\d\d|\d{4,})(\.\d+)?)$/,
                        regexErrorMessage: t(
                            "Tare Weight must be a valid number and must be 500 or more"
                        ),
                        onChange: (value) =>
                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addTruckForm", fieldName: "tareWeight", value },
                            }),
                    },
                    {
                        name: "capacity",
                        label: "Vehicle Load Capacity",
                        type: "number",
                        required: true,
                        regex: /^(?:\d+(\.\d+)?|\.\d+)$|^$/,
                        regexErrorMessage: t("Capacity must be a positive number."),
                        onChange: (value) =>
                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addTruckForm", fieldName: "capacity", value },
                            }),
                    },
                    {
                        name: "capacityUnit",
                        label: "Vehicle Load Capacity UOM",
                        type: "dropdown",
                        required: true,
                        options: state.ui.capacityUnitOptions,
                        isLoading: state.loading.isCapacityUnitLoading,
                        onChange: (value) => {
                            const v = normalizeDropdownValue(value);
                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addTruckForm", fieldName: "capacityUnit", value: v },
                            });
                        },
                    },
                    {
                        name: "vehicleSerialNumber",
                        label: "Vehicle Serial Number",
                        type: "text",
                        required: true,
                        regex: /^[0-9]{8,}$/,
                        regexErrorMessage: t(
                            "Vehicle Serial Number must contain only English digits and be at least 8 characters long."
                        ),
                        onChange: (value) =>
                            dispatchState({
                                type: "UPDATE_FORM_FIELD",
                                payload: { formName: "addTruckForm", fieldName: "vehicleSerialNumber", value },
                            }),
                    },
                ]}
            />

            {/* Backdrop */}
            <LoadingBackdrop
                open={state.loading.isLoadingBackdrop}
                message={t("Loading data, please wait.")}
                variant="bars"
                color="greenGradient"
            />
        </Box>
    );
    // #endregion
}
